import React, {useCallback, useEffect, useState} from 'react'
import {Button, Label, Loading, Modal} from '@therms/atalaya'
import {
  Alerts,
  ReportStateBadge,
  RPCDocs,
  useRPCContext,
} from '@therms/react-modules'

interface ChangeReportStatusModalProps {
  closeHandler: () => void
  onComplete: () => void
  report: RPCDocs.Reports.GetReport.v1.Report
}

function ChangeReportStatusModal({
  closeHandler,
  onComplete,
  report,
}: ChangeReportStatusModalProps) {
  const [statuses, setStatuses] = useState<
    RPCDocs.Reports.GetReportNextStatuses.v1.Data['statuses']
  >([])
  const [loading, setLoading] = useState(true)
  const {rpcService} = useRPCContext()

  useEffect(() => {
    rpcService.Reports.GetReportNextStatuses.v1
      .call({
        reportId: report.id,
      })
      .then(({data}) => setStatuses(data.statuses))
      .catch((err: any) => {
        console.error('Reports.GetReportNextStatuses.v1 err', err)

        Alerts.error(
          `There was a problem getting the Report status details, please try again.`,
          err.message,
        )
      })
      .finally(() => setLoading(false))
  }, [report])

  const handleChangeStatus = useCallback(
    (statusId: string) => {
      setLoading(true)

      rpcService.Reports.ChangeReportStatus.v1
        .call({
          newStatusId: statusId,
          reportId: report.id,
        })
        .then(() => {
          Alerts.success(`Report status changed`)

          onComplete()
        })
        .catch((err: any) => {
          console.error('Reports.ChangeReportStatus.v1 err', err)

          Alerts.error(
            `There was a problem changing the Report status details, please try again.`,
            err.message,
          )
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [report],
  )

  return (
    <Modal
      closeHandler={closeHandler}
      escPressClose
      header="Change Report Status"
    >
      <div className="mb-3">
        <Label>Current Status</Label> <ReportStateBadge state={report.state} />{' '}
        {report.status.title}
      </div>

      {loading && <Loading overlay />}

      {!statuses?.length && !loading && (
        <div className="py-5 text-center italic">
          This Report's status cannot be changed.
        </div>
      )}

      <div className="gap-y-sm mt-8 flex flex-col">
        {statuses?.map((status) => (
          <Button
            className="py-3"
            onClick={() => handleChangeStatus(status.id)}
            fullWidth
            variant="main"
            key={status.id}
          >
            <div className="grid grid-cols-2">
              <div className="text-right">
                <ReportStateBadge state={status.state} />
              </div>

              <div className="ml-1 text-left">{status.title}</div>
            </div>
          </Button>
        ))}
      </div>
    </Modal>
  )
}

export {ChangeReportStatusModal}
